import React, { useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues, Path, PathValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import {
    DateTimePickerHandle,
    DateTimePickerProps as KendoDateTimePickerProps,
    DateTimePicker as KendoDateTimePicker,
    DateInput,
    DateTimePickerChangeEvent,
    DateInputProps,
    DateInputChangeEvent,
    DateTimePickerOpenEvent,
    DateTimePickerCloseEvent,
} from '@progress/kendo-react-dateinputs';
import { InputSuffix } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { xIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import moment from 'moment';
import svgStyles from '@components-clean/core/drag-drop-lists-core/style.module.scss';
import ValidationIconSuffix from 'views-clean/components/core/icons/validation-icon-suffix';

interface DateTimePickerProps extends Omit<KendoDateTimePickerProps, 'min' | 'max'> {
    'data-testid': string;
    inline?: boolean;
    fillMode: 'solid' | 'flat';
    readonly?: boolean;
    min?: Date | 'future';
    max?: Date | 'past';
    showPlaceholder?: boolean;
}

const TestInput = ({
    value,
    disabled,
    onChange,
    valid,
    validationMessage,
    placeholder,
    ...inputProps
}: DateInputProps) => {
    return (
        <>
            <DateInput
                {...inputProps}
                value={value}
                onChange={onChange}
                disabled={disabled}
                placeholder={!disabled ? placeholder : null}
            />
            {!!value && !disabled && (
                <InputSuffix
                    onClick={() => {
                        onChange && onChange({ value: null } as DateInputChangeEvent);
                    }}
                >
                    <SvgIcon icon={xIcon} size="large" className={svgStyles.svgClear} />
                </InputSuffix>
            )}
            {valid === false && <ValidationIconSuffix validationMessage={validationMessage} />}
        </>
    );
};

const ReadonlyTestInput = ({ ...inputProps }: DateInputProps) => {
    return <DateInput {...inputProps} readonly onChange={() => null} />;
};

const DateFormat = 'dd-MMM-yyyy HH:mm';
const DisabledDateFormat = 'dd MMM yyyy H mm';

const DateTimePicker = ({
    'data-testid': testId,
    onClose,
    onOpen,
    className,
    onChange,
    value,
    disabled,
    fillMode,
    label,
    min,
    max,
    readonly,
    inline = false,
    placeholder,
    showPlaceholder = true,
    ...props
}: DateTimePickerProps) => {
    const ref = useRef<DateTimePickerHandle>(null);
    const show = readonly ? false : props.show;

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        ref.current?.element?.setAttribute('data-testid', testId);
    }, []);

    const hasMinOrMax = min !== undefined || max !== undefined;
    const realOnChange = (event: DateTimePickerChangeEvent) => {
        const minToUse = min === 'future' ? moment().toDate() : min;
        const maxToUse = max === 'past' ? moment().toDate() : max;

        if (
            hasMinOrMax &&
            onChange &&
            event.target.value !== null &&
            event.target.value !== undefined &&
            event.target.value !== value
        ) {
            if (minToUse && event.target.value < minToUse) {
                onChange({ ...event, target: { value: minToUse } } as DateTimePickerChangeEvent);
                return;
            }
            if (maxToUse && event.target.value > maxToUse) {
                onChange({ ...event, target: { value: maxToUse } } as DateTimePickerChangeEvent);
                return;
            }
        }
        if (event.target.value !== value && onChange) {
            onChange(event);
        }
    };

    // Add an open handler so that we can add a class to determine if the combo box is open
    // This is needed so that we can override the style when it's open
    const onOpenHandler = (event: DateTimePickerOpenEvent) => {
        setIsOpen(true);

        if (onOpen) {
            onOpen(event);
        }
    };

    // Add a close handler so that we can remove a class to determine if the combo box is open
    // This is needed so that we can override the style when it's open
    const onCloseHandler = (event: DateTimePickerCloseEvent) => {
        setIsOpen(false);

        if (onClose) {
            onClose(event);
        }
    };

    const classes = cn(className, { 'opra-dateinput-open': !readonly && isOpen, hint: !value });

    return (
        <div>
            <Label
                editorDisabled={disabled}
                editorRef={ref}
                className={cn(inline ? ['!k-pb-0', 'k-white-space-nowrap'] : null)}
            >
                {label}
            </Label>
            <KendoDateTimePicker
                {...props}
                className={classes}
                onOpen={onOpenHandler}
                onClose={onCloseHandler}
                ref={ref}
                format={disabled && !value ? DisabledDateFormat : DateFormat}
                formatPlaceholder={
                    disabled
                        ? { day: ' ', month: ' ', year: ' ', hour: ' ', minute: ' ' }
                        : { day: 'DD', month: 'Mmm', year: 'YYYY', hour: 'HH', minute: 'mm' }
                }
                placeholder={showPlaceholder ? (placeholder ?? 'DD-Mmm-YYYY HH:mm') : ''}
                min={min === 'future' ? moment().toDate() : min}
                max={max === 'past' ? moment().toDate() : max}
                fillMode={fillMode}
                disabled={disabled}
                onChange={(event) => realOnChange(event)}
                value={value}
                dateInput={readonly ? ReadonlyTestInput : TestInput}
                show={show}
            />
        </div>
    );
};

export interface DateTimePickerControllerProps<TControl extends FieldValues> extends DateTimePickerProps {
    control: Control<TControl>;
    name: FieldPath<TControl>;
}

const DateTimePickerController = <TControl extends FieldValues>({
    control,
    name,
    disabled = false,
    'data-testid': dataTestId,
    label,
    min,
    max,
    required = false,
    readonly = false,
    fillMode = 'solid',
    placeholder,
    defaultValue,
    showPlaceholder = true,
    ...props
}: DateTimePickerControllerProps<TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
            }}
            defaultValue={defaultValue as PathValue<TControl, Path<TControl>>}
            render={({ field: { onChange, value, name: fieldName }, fieldState: { error } }) => {
                return (
                    <DateTimePicker
                        name={fieldName}
                        label={label}
                        data-testid={dataTestId}
                        value={value as Date | null}
                        fillMode={fillMode}
                        onChange={onChange}
                        valid={!error}
                        validationMessage={error?.message}
                        placeholder={placeholder}
                        min={min}
                        max={max}
                        disabled={disabled}
                        readonly={readonly}
                        showPlaceholder={showPlaceholder}
                        {...props}
                    />
                );
            }}
        />
    );
};

export { DateTimePicker, DateTimePickerController };
